// SAFE-WRITE-BEGIN: frontend/src/components/admin/SnapshotPaymentsSection.tsx
/**
 * ADD_VAL-SNAPSHOT-2-PAIDBUTTON — "Snapshot payments", under the entitlements matrix.
 *
 * One row per engagement with an owner-recorded estimate. Super_admin only: the
 * route is 403 for everyone else, and this is the only place a Snapshot price is
 * shown (owner ruling 2026-09-20). The server decides what is legal; this section
 * renders its rows and asks for a reason.
 */
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatMediumDate } from '@/utils/date'

export interface SnapshotPaymentRow {
  engagement_id: string
  company: string
  owner_email: string
  estimate_date: string | null
  status: 'awaiting' | 'paid'
  product: string | null
  label: string | null
  price: number | null
  paid_on: string | null
  credit_until: string | null
  email_to_masked?: string
  emailed_on?: string | null
  /** VAL-EMAIL-ENTRY-1: false = no client email on file, so the sender types one. */
  has_email?: boolean
  /** FIX_VAL-SNAPSHOT-ADMIN-EMAIL-2: false = the Snapshot cannot be printed yet; print_problem says why. */
  can_print?: boolean
  print_problem?: string | null
}

const stem = (reason: string) => reason.trim().replace(/\.$/, '')
const cannotPrint = (r: SnapshotPaymentRow) => r.can_print === false

const HELP = "No client email on file. Enter the client's email to send the PDF. It is saved on this engagement."
const INVALID = 'Enter one valid email address.'
const validEmail = (v: string) => /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>.]{2,}$/.test(v.trim())

interface Option {
  product: string
  label: string
  price: number
}

interface Listing {
  rows: SnapshotPaymentRow[]
  options: Option[]
  suggested_reasons: string[]
}

const BASE = '/v1/admin/snapshot-payments'
const DEFAULT_REASON = 'paid-manual'

export function statusText(r: SnapshotPaymentRow): string {
  if (r.status !== 'paid') return 'Awaiting payment'
  return `Paid ${formatMediumDate(r.paid_on)}, ${r.label} $${r.price}, credit until ${formatMediumDate(r.credit_until)}`
}

type Pending = { kind: 'pay' | 'undo'; row: SnapshotPaymentRow }

export function SnapshotPaymentsSection() {
  const [data, setData] = useState<Listing | null>(null)
  const [failed, setFailed] = useState(false)
  const [pending, setPending] = useState<Pending | null>(null)
  const [product, setProduct] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [emailClient, setEmailClient] = useState(true)
  const [notEmailed, setNotEmailed] = useState<Record<string, string>>({})

  function noteEmail(id: string, sent: boolean, message?: string) {
    setNotEmailed(prev => {
      const next = { ...prev }
      if (sent) delete next[id]
      else next[id] = message ?? 'The email could not be sent.'
      return next
    })
  }
  const [mailing, setMailing] = useState<SnapshotPaymentRow | null>(null)
  const [sending, setSending] = useState<SnapshotPaymentRow | null>(null)
  const [typed, setTyped] = useState('')
  const [invalid, setInvalid] = useState(false)
  const [confirming, setConfirming] = useState(false)

  const load = useCallback(() => {
    apiClient
      .get<Listing>(BASE)
      .then(r => { setData(r.data); setFailed(false) })
      .catch(() => setFailed(true))
  }, [])

  useEffect(() => { load() }, [load])

  function open(kind: 'pay' | 'undo', row: SnapshotPaymentRow) {
    setPending({ kind, row })
    setProduct(data?.options[0]?.product ?? '')
    setReason(kind === 'pay' ? DEFAULT_REASON : '')
    setEmailClient(true)
  }

  async function confirm() {
    if (!pending) return
    const trimmed = reason.trim()
    if (!trimmed) { toast.error('A reason is required.'); return }
    setBusy(true)
    try {
      const id = pending.row.engagement_id
      if (pending.kind === 'pay') {
        const res = await apiClient.post<{ email?: { sent: boolean; message: string } }>(
          `${BASE}/mark-paid`,
          { engagement_id: id, product, reason: trimmed, email_client: emailClient },
        )
        toast.success('Payment recorded.')
        const mail = res?.data?.email
        if (mail) {
          (mail.sent ? toast.success : toast.error)(mail.message)
          noteEmail(id, mail.sent, mail.message)
        }
      } else {
        await apiClient.post(`${BASE}/undo`, { engagement_id: id, reason: trimmed })
        toast.success('Payment undone.')
      }
      setPending(null)
      load()
    } catch {
      toast.error(pending.kind === 'pay' ? 'Failed to record payment.' : 'Failed to undo.')
    } finally {
      setBusy(false)
    }
  }

  function closeMailing() {
    setMailing(null); setTyped(''); setInvalid(false); setConfirming(false)
  }

  function startEmail(row: SnapshotPaymentRow) {
    if (row.has_email === false) { setMailing(row); return }
    setSending(row)
  }

  async function emailNow(row: SnapshotPaymentRow, clientEmail?: string) {
    setBusy(true)
    try {
      await apiClient.post(
        `${BASE}/email`,
        clientEmail ? { engagement_id: row.engagement_id, client_email: clientEmail } : { engagement_id: row.engagement_id },
      )
      toast.success('Emailed.')
      noteEmail(row.engagement_id, true)
      setSending(null)
      closeMailing()
      load()
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      const message = typeof detail === 'string' ? detail : 'The email could not be sent.'
      toast.error(message)
      noteEmail(row.engagement_id, false, message)
      setSending(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="mt-10" data-testid="snapshot-payments">
      <h2 className="text-xl font-semibold text-gray-900">Snapshot payments</h2>
      <p className="mt-1 text-sm text-gray-500">
        Engagements with an owner-recorded estimate. Undo revokes only that engagement's access.
      </p>
      {failed && <p className="mt-3 text-sm text-red-600">Failed to load snapshot payments.</p>}
      {data && data.rows.length === 0 && (
        <p className="mt-3 text-sm text-gray-500">No owner estimates yet.</p>
      )}
      {data && data.rows.length > 0 && (
        <table className="mt-3 w-full text-left text-sm">
          <thead>
            <tr className="border-b text-xs uppercase text-gray-500">
              <th className="py-2 pr-3">Company</th>
              <th className="py-2 pr-3">Owner</th>
              <th className="py-2 pr-3">Estimate date</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.engagement_id} className="border-b" data-testid={`snap-row-${r.engagement_id}`}>
                <td className="py-2 pr-3">{r.company}</td>
                <td className="py-2 pr-3">{r.owner_email}</td>
                <td className="py-2 pr-3">{formatMediumDate(r.estimate_date)}</td>
                <td className="py-2 pr-3">
                  {statusText(r)}
                  {notEmailed[r.engagement_id] && (
                    <div className="text-xs text-red-600" data-testid={`snap-not-emailed-${r.engagement_id}`}>
                      Not emailed: {notEmailed[r.engagement_id]}
                    </div>
                  )}
                </td>
                <td className="py-2 text-right">
                  {r.status === 'paid' ? (
                    <span className="inline-flex items-center gap-2">
                      {r.emailed_on && (
                        <span className="text-xs text-gray-500">Emailed {formatMediumDate(r.emailed_on)}</span>
                      )}
                      {r.email_to_masked && (
                        <span className="text-xs text-gray-500">{r.email_to_masked}</span>
                      )}
                      {cannotPrint(r) ? (
                        <span className="text-xs text-red-600" data-testid={`snap-cannot-produce-${r.engagement_id}`}>
                          This snapshot can't be produced yet: {stem(r.print_problem ?? '')}.
                        </span>
                      ) : (
                        <Button variant="outline" size="sm" disabled={busy} onClick={() => startEmail(r)}>
                          Email to client
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => open('undo', r)}>Undo</Button>
                    </span>
                  ) : (
                    <Button size="sm" onClick={() => open('pay', r)}>Mark payment received</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={!!sending} onOpenChange={o => !o && setSending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email to client{sending ? ` — ${sending.company}` : ''}</DialogTitle>
          </DialogHeader>
          <p className="text-sm">The snapshot is ready. Email the PDF to {sending?.email_to_masked ?? 'the client'}?</p>
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setSending(null)}>Back</Button>
            <Button disabled={busy} onClick={() => sending && emailNow(sending)}>Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!mailing} onOpenChange={o => !o && closeMailing()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email to client{mailing ? ` — ${mailing.company}` : ''}</DialogTitle>
          </DialogHeader>
          {confirming ? (
            <p className="text-sm">
              Send the PDF to {typed.trim().toLowerCase()}. It will be saved as this engagement's client email.
            </p>
          ) : (
            <div className="space-y-1">
              <Label htmlFor="snap-client-email">Client email</Label>
              <Input
                id="snap-client-email"
                type="email"
                value={typed}
                onChange={e => { setTyped(e.target.value); setInvalid(false) }}
              />
              {invalid
                ? <p className="text-xs text-red-700">{INVALID}</p>
                : <p className="text-xs text-gray-600">{HELP}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => (confirming ? setConfirming(false) : closeMailing())}>
              {confirming ? 'Back' : 'Cancel'}
            </Button>
            {confirming ? (
              <Button disabled={busy} onClick={() => mailing && emailNow(mailing, typed.trim().toLowerCase())}>Send</Button>
            ) : (
              <Button onClick={() => (validEmail(typed) ? setConfirming(true) : setInvalid(true))}>Continue</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pending} onOpenChange={o => !o && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pending?.kind === 'pay' ? 'Mark payment received' : 'Undo payment'}
              {pending ? ` — ${pending.row.company}` : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {pending?.kind === 'pay' && (
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Snapshot paid for</legend>
                {(data?.options ?? []).map(o => (
                  <label key={o.product} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="snapshot-product"
                      checked={product === o.product}
                      onChange={() => setProduct(o.product)}
                    />
                    {o.label} ${o.price}
                  </label>
                ))}
              </fieldset>
            )}
            {pending?.kind === 'pay' && cannotPrint(pending.row) && (
              <p className="text-sm text-red-700" data-testid="snap-cannot-print">
                {`This snapshot can't be printed yet: ${stem(pending.row.print_problem ?? '')}. You can still record the payment.`}
              </p>
            )}
            {pending?.kind === 'pay' && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={emailClient}
                  onChange={e => setEmailClient(e.target.checked)}
                />
                Email the snapshot to the client
              </label>
            )}
            <div>
              <Label htmlFor="snap-reason">Reason *</Label>
              <Input
                id="snap-reason"
                value={reason}
                onChange={e => setReason(e.target.value)}
                data-testid="snap-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)}>Cancel</Button>
            <Button disabled={busy} onClick={confirm}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
// SAFE-WRITE-EOF: frontend/src/components/admin/SnapshotPaymentsSection.tsx
